import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../shared/material.module';
import { PyramidalShareholderService } from '../../../services/pyramidal-shareholder.service';
import { Helpers } from '../../../utilities/helpers';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { CreatePyramidalShareholderRequest } from '../../../models/api/createPyramidalShareholderRequest';
import { UpsertMultiYearPyramidalShareholderPyramidRequest, PyramidalShareholderYearPercentage } from '../../../models/api/upsertMultiYearPyramidalShareholderPyramidRequest';
import { HotToastService } from '@ngxpert/hot-toast';
import { MatDialogRef } from '@angular/material/dialog';
import { switchMap } from 'rxjs';
import { FormPyramidalPyramidItemComponent } from '../form-pyramidal-pyramid-item/form-pyramidal-pyramid-item.component';

@Component({
  selector: 'app-add-pyramidal-pyramid-item',
  standalone: true,
  imports: [
    CommonModule,
    NgxSpinnerModule,
    MaterialModule,
    FormPyramidalPyramidItemComponent
  ],
  templateUrl: './add-pyramidal-pyramid-item.component.html',
  styleUrls: ['./add-pyramidal-pyramid-item.component.scss']
})
export class AddPyramidalPyramidItemComponent implements OnInit {
  @ViewChild(FormPyramidalPyramidItemComponent) formComponent!: FormPyramidalPyramidItemComponent;

  @Input() prefilledName?: string;
  @Input() prefilledCategoryId?: string;
  @Input() prefilledSubCategoryId?: string;
  @Input() pyramidalShareholderId!: string;
  @Input() connectedEntityId?: string;
  @Input() connectedEntityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director';
  @Input() isCreatingNewPyramidalShareholder: boolean = false;
  @Input() dialogRef?: MatDialogRef<any>;
  @Output() formValidityChanged = new EventEmitter<boolean>();

  measurements: string[] = [];

  private pyramidalShareholderService = inject(PyramidalShareholderService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationDialogService = inject(ConfirmationDialogService);

  constructor() {
    // Load years from measurements helper
    this.measurements = Helpers.measurements();
  }

  ngOnInit(): void {
  }

  onFormValidityChanged(isValid: boolean): void {
    this.formValidityChanged.emit(isValid);
  }

  onConfirm(): void {
    if (!this.formComponent.isValid()) {
      this.toast.error('Please fill in all required fields');
      return;
    }

    const dialogObservable = this.confirmationDialogService.openCreateConfirmationDialog();

    dialogObservable.subscribe((result: boolean) => {
      if (result) {
        this.submitPyramid();
      }
    });
  }

  private submitPyramid(): void {
    const formData = this.formComponent.getFormValue();

    // Extract year-percentage pairs from the form
    const yearPercentages: PyramidalShareholderYearPercentage[] = [];
    this.measurements.forEach(measurement => {
      const percentage = formData[`directPercentage_${measurement}`];
      if (percentage !== null && percentage !== undefined && percentage !== '') {
        yearPercentages.push({
          year: parseInt(measurement, 10),
          percentage: parseFloat(percentage)
        });
      }
    });

    this.spinnerService.show();

    // If we're creating a new pyramidal shareholder, do that first
    if (this.isCreatingNewPyramidalShareholder) {
      this.createPyramidalShareholderThenPyramid(formData, yearPercentages);
    } else {
      // Otherwise, just create the pyramid entries
      this.createPyramidEntries(formData, yearPercentages, this.connectedEntityId, this.connectedEntityType);
    }
  }

  private createPyramidalShareholderThenPyramid(formData: any, yearPercentages: PyramidalShareholderYearPercentage[]): void {
    // Build request to create pyramidal shareholder
    const createPyramidalShareholderRequest: CreatePyramidalShareholderRequest = {
      name: formData.pyramidName,
      categoryId: formData.category?.id || '',
      subCategoryId: formData.subcategory?.id || '',
      notes: ''
    };

    // First, create the pyramidal shareholder
    this.pyramidalShareholderService.createPyramidalShareholder(createPyramidalShareholderRequest)
      .pipe(
        switchMap(pyramidalShareholder => {
          // Then create pyramid entries using the newly created pyramidal shareholder ID
          return this.createPyramidEntriesObservable(formData, yearPercentages, pyramidalShareholder.id, 'pyramidalShareholder');
        })
      )
      .subscribe({
        next: (pyramids) => {
          this.spinnerService.hide();
          this.toast.success('Pyramidal shareholder and pyramid entries added successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });

          // Close the dialog and return the result
          if (this.dialogRef) {
            this.dialogRef.close({ success: true, pyramids });
          }
        },
        error: (error) => {
          this.spinnerService.hide();
          console.error('Error creating pyramidal shareholder and pyramid entries:', error);
          this.toast.error('Failed to create pyramidal shareholder and pyramid entries');
        }
      });
  }

  private createPyramidEntries(
    formData: any,
    yearPercentages: PyramidalShareholderYearPercentage[],
    entityId?: string,
    entityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director'
  ): void {
    this.createPyramidEntriesObservable(formData, yearPercentages, entityId, entityType).subscribe({
      next: (pyramids) => {
        this.spinnerService.hide();
        this.toast.success('Pyramid entries added successfully', {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF',
          }
        });

        // Close the dialog and return the result
        if (this.dialogRef) {
          this.dialogRef.close({ success: true, pyramids });
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Error adding pyramid entries:', error);
        this.toast.error('Failed to add pyramid entries');
      }
    });
  }

  private createPyramidEntriesObservable(
    formData: any,
    yearPercentages: PyramidalShareholderYearPercentage[],
    entityId?: string,
    entityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director'
  ) {
    // Build the request object
    const request: UpsertMultiYearPyramidalShareholderPyramidRequest = {
      pyramidalShareholderId: this.pyramidalShareholderId,
      yearPercentages: yearPercentages,
      categoryId: formData.category?.id || undefined,
      subCategoryId: formData.subcategory?.id || undefined
    };

    // Set the appropriate entity ID based on the connection type
    if (entityId && entityType) {
      switch (entityType) {
        case 'largeShareholder':
          request.largeShareholderId = entityId;
          break;
        case 'pyramidalShareholder':
          request.pyramidPyramidalShareholderId = entityId;
          break;
        case 'company':
          request.companyId = entityId;
          break;
        case 'opbCompany':
          request.opbCompanyId = entityId;
          break;
        case 'director':
          request.directorId = entityId;
          break;
      }
    }

    return this.pyramidalShareholderService.upsertMultiYearPyramid(request);
  }
}
